import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import BlogCard from '../components/BlogCard';
import { blogPosts, stats, categories, courses } from '../data';
import '../CSS/Home.css';

const Home = () => {
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (email) {
      setSubscribed(true);
      setEmail('');
    }
  };

  return (
    <main className="home">

      {/* ── Hero ────────────────────────────────── */}
      <section className="hero">
        <div className="hero__glow hero__glow--left" />
        <div className="hero__glow hero__glow--right" />

        <div className="container hero__inner">
          <div className="hero__badge animate-fade-up">
            <span className="hero__badge-dot" />
            Writing about Web Development since 2022
          </div>

          <h1 className="hero__title animate-fade-up-delay-1">
            Hi, I'm <span className="hero__name-gradient">Vijay</span>
            <br />
            <span className="hero__subtitle-line">Full-Stack Web Developer</span>
          </h1>

          <p className="hero__description animate-fade-up-delay-2">
            I write about <strong>React</strong>, <strong>Node.js</strong>, <strong>TypeScript</strong>,
            and modern web architecture. Real benchmarks. Honest opinions. Production-tested code.
          </p>

          <div className="hero__tags animate-fade-up-delay-2">
            {categories.map((cat) => (
              <Link key={cat.slug} to={`/blog?category=${cat.slug}`} className="hero__tag">
                <span>{cat.icon}</span> {cat.label}
              </Link>
            ))}
          </div>

          <div className="hero__actions animate-fade-up-delay-3">
            <Link to="/blog" className="btn btn--primary">
              Explore Articles
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </Link>
            <Link to="/courses" className="btn btn--secondary">
              Free Courses
            </Link>
          </div>

          <div className="hero__stats animate-fade-up-delay-4">
            {stats.map((s) => (
              <div key={s.label} className="hero__stat">
                <span className="hero__stat-value">{s.value}</span>
                <span className="hero__stat-label">{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Popular Articles ─────────────────────── */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <div className="section-header__left">
              <span className="section-label">Popular Content</span>
              <h2 className="section-title">Most-Read Articles</h2>
              <p className="section-desc">
                Deep dives into React, Node.js, and modern architecture — with benchmarks, code, and real opinions.
              </p>
            </div>
            <Link to="/blog" className="btn btn--ghost">
              Browse all articles
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </Link>
          </div>

          <div className="articles-grid">
            {blogPosts.slice(0, 4).map((post, i) => (
              <BlogCard key={post.id} post={post} featured={i === 0} />
            ))}
          </div>
        </div>
      </section>

      {/* ── Categories ───────────────────────────── */}
      <section className="section section--dark">
        <div className="container">
          <div className="section-header section-header--center">
            <span className="section-label">Browse by Topic</span>
            <h2 className="section-title">What I Write About</h2>
          </div>
          <div className="categories-grid">
            {categories.map((cat) => (
              <Link key={cat.slug} to={`/blog?category=${cat.slug}`} className="category-card">
                <span className="category-card__icon">{cat.icon}</span>
                <span className="category-card__label">{cat.label}</span>
                <span className="category-card__count">{cat.count} articles</span>
                <svg className="category-card__arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── Courses ──────────────────────────────── */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <div className="section-header__left">
              <span className="section-label">Learn</span>
              <h2 className="section-title">Featured Courses</h2>
              <p className="section-desc">
                Structured learning paths for modern web development, from beginner to production-ready.
              </p>
            </div>
            <Link to="/courses" className="btn btn--ghost">
              All courses
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </Link>
          </div>
          <div className="courses-grid">
            {courses.map((course) => (
              <Link key={course.id} to={`/courses/${course.slug}`} className="course-card">
                <div className="course-card__cover" style={{ background: course.gradient }}>
                  <span className="course-card__icon">{course.icon}</span>
                  {course.badge && (
                    <span className="course-card__badge" style={{ background: course.badgeColor }}>
                      {course.badge}
                    </span>
                  )}
                </div>
                <div className="course-card__body">
                  <div className="course-card__tags">
                    {course.tags.map(t => (
                      <span key={t} className="blog-card__tag">{t}</span>
                    ))}
                  </div>
                  <h3 className="course-card__title">{course.title}</h3>
                  <p className="course-card__desc">{course.description}</p>
                  <div className="course-card__meta">
                    <span>📚 {course.lessons} lessons</span>
                    <span>⏱ {course.duration}</span>
                    <span>👥 {course.students}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── Newsletter ───────────────────────────── */}
      <section className="section">
        <div className="container">
          <div className="newsletter-section">
            <div className="newsletter-section__glow" />
            <div className="newsletter-section__content">
              <span className="section-label">Newsletter</span>
              <h2 className="newsletter-section__title">Stay Ahead in Web Dev</h2>
              <p className="newsletter-section__desc">
                Once-weekly email. Best insights on React, Node.js, and modern JavaScript. No fluff.
              </p>

              {subscribed ? (
                <div className="newsletter-section__success">
                  <span>🎉</span> You're in! Check your email for confirmation.
                </div>
              ) : (
                <form className="newsletter-section__form" onSubmit={handleSubscribe}>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email address"
                    className="newsletter-section__input"
                    required
                  />
                  <button type="submit" className="btn btn--primary">
                    Subscribe Free
                  </button>
                </form>
              )}

              <p className="newsletter-section__note">
                Join <strong>6,000+</strong> developers · Every Tuesday · Free forever
              </p>
            </div>
          </div>
        </div>
      </section>

    </main>
  );
};

export default Home;
